import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import env from '@jwp/ott-common/src/env';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import {
  GET_ALL_MEDIA_APPROVALS_LIST,
  GetAllMediaApprovalsListData,
  GetAllMediaApprovalsListVariables,
  MediaApprovalMaType,
} from '@jwp/ott-common/src/graphql/query/moderate.queries';
import { formatDateDDMMYYYY, subDays, startOfDay, formatToLocalDateTime } from '@jwp/ott-common/src/utils/datetime';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import NoResultsIcon from '@jwp/ott-theme/assets/icons/no_result.svg?react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { PATH_HOME } from '@jwp/ott-common/src/paths';
import { useModerateStore } from '@jwp/ott-common/src/stores/ModerateStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { ROLES, PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';

import IconButton from '../IconButton/IconButton';
import Skeleton from '../Skeleton/Skeleton';
import Button from '../Button/Button';
import { formatDurationWithLabelsMs } from '../../utils/formatTime';

import styles from './ModerateContent.module.scss';
import ModerateContentFilter, { DateRangeType } from './ModerateContentFilter';

const DEFAULT_CONTENT_TYPES: MediaApprovalMaType[] = ['REELS', 'VIDEOS'];

const TABLE_COLUMNS = ['Thumbnail', 'Creator', 'Content Type', 'Duration', 'Uploaded Date', 'Status', 'Action'] as const;

function getDateRangeParams(range: DateRangeType) {
  const now = new Date();
  switch (range) {
    case 'today':
      return {
        fromDate: formatToLocalDateTime(startOfDay(now)),
        toDate: formatToLocalDateTime(now),
      };
    case 'lastWeek':
      return {
        fromDate: formatToLocalDateTime(subDays(now, 7)),
        toDate: formatToLocalDateTime(now),
      };
    case 'lastMonth':
      return {
        fromDate: formatToLocalDateTime(subDays(now, 30)),
        toDate: formatToLocalDateTime(now),
      };
    case 'oldest':
    default:
      return {
        fromDate: undefined,
        toDate: undefined,
      };
  }
}

const ModerateContent = () => {
  const { t } = useTranslation(['user', 'video', 'common', 'upload']);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAccountStore((state) => state.user);
  const { isModerateContentEnabled } = useAppConfig();

  // Persistent Filter States
  const { filters, setFilters } = useModerateStore();
  const { dateRange: appliedDateRange, contentTypes: appliedContentTypes, categories: appliedCategories } = filters;

  const dateRangeParams = React.useMemo(() => getDateRangeParams(appliedDateRange), [appliedDateRange]);
  const queryVariables = React.useMemo<GetAllMediaApprovalsListVariables>(
    () => ({
      first: 10,
      offset: 0,
      maType: appliedContentTypes.length > 0 ? appliedContentTypes : DEFAULT_CONTENT_TYPES,
      maCategoryOrGenre: appliedCategories.length > 0 ? appliedCategories : undefined,
      fromDate: dateRangeParams.fromDate,
      toDate: dateRangeParams.toDate,
      ...(appliedDateRange === 'oldest' ? { orderBy: [{ field: 'maUpdatedAt', direction: 'ASC' as const }] } : {}),
    }),
    [appliedContentTypes, appliedCategories, dateRangeParams, appliedDateRange],
  );

  const { data, loading, error, fetchMore } = useQuery<GetAllMediaApprovalsListData, GetAllMediaApprovalsListVariables>(GET_ALL_MEDIA_APPROVALS_LIST, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    if (!loading && (!isModerateContentEnabled || !hasPermission(user, PERMISSIONS.APPROVALS_MEDIA))) {
      navigate(PATH_HOME, { replace: true });
    }
  }, [isModerateContentEnabled, user, loading, navigate]);

  const handleBackClick = () => {
    navigate(PATH_HOME);
  };

  const handleReview = (id: string, contentType: string) => {
    useUploadStore.getState().setInitiated(true);
    if (contentType === 'Video') {
      navigate(createURLFromLocation(location, { u: 'video-review', id }));
    } else {
      navigate(createURLFromLocation(location, { u: 'reel-review', id }));
    }
  };

  const handleViewDetails = (id: string, contentType: string) => {
    useUploadStore.getState().setInitiated(true);
    if (contentType === 'Video') {
      navigate(createURLFromLocation(location, { u: 'video-details', id }));
    } else {
      navigate(createURLFromLocation(location, { u: 'reel-details', id }));
    }
  };

  const handleApplyFilters = (dateRange: DateRangeType, contentTypes: MediaApprovalMaType[], categories: string[]) => {
    setFilters({
      dateRange,
      contentTypes,
      categories,
    });
  };

  const [loadingMore, setLoadingMore] = React.useState(false);

  React.useEffect(() => {
    setLoadingMore(false);
  }, [queryVariables]);

  const nodes = data?.getAllMediaApprovalsList?.nodes || [];
  const totalCount = data?.getAllMediaApprovalsList?.totalCount || 0;
  const hasMore = nodes.length < totalCount;
  const isEmpty = !loading && nodes.length === 0;

  const handleLoadMore = React.useCallback(async () => {
    if (!data || loadingMore || !hasMore) { return; }

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          ...queryVariables,
          offset: data.getAllMediaApprovalsList.nodes.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) { return prev; }
          return {
            getAllMediaApprovalsList: {
              ...fetchMoreResult.getAllMediaApprovalsList,
              nodes: [...prev.getAllMediaApprovalsList.nodes, ...fetchMoreResult.getAllMediaApprovalsList.nodes],
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [data, fetchMore, hasMore, loadingMore, queryVariables]);

  const handleTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom < 80 && hasMore && !loading && !loadingMore) {
      handleLoadMore();
    }
  };

  const renderRows = () => {
    if (loading && nodes.length === 0) {
      return ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'].map((key) => (
        <tr key={key}>
          <td>
            <div className={styles.thumbnailCell}>
              <Skeleton width="30px" height="52px" borderRadius="4px" />
              <Skeleton width="120px" height="16px" />
            </div>
          </td>
          <td>
            <Skeleton width="100px" height="16px" />
          </td>
          <td>
            <Skeleton width="60px" height="16px" />
          </td>
          <td>
            <Skeleton width="50px" height="16px" />
          </td>
          <td>
            <Skeleton width="80px" height="16px" />
          </td>
          <td>
            <Skeleton width="70px" height="16px" />
          </td>
          <td>
            <Skeleton width="80px" height="32px" borderRadius="16px" />
          </td>
        </tr>
      ));
    }

    const rows = nodes.map((node) => {
      const contentTypeMap: Record<string, string> = {
        reels: 'Reel',
        videos: 'Video',
      };
      const contentType = contentTypeMap[node?.maType?.toLocaleLowerCase()] || 'Video';

      return (
        <tr key={node?.maId}>
          <td>
            <div className={styles.thumbnailCell}>
              <img
                src={env.APP_BASE_URL + node?.maS3ThumbnailKeyUrl}
                alt={node?.maTitle}
                className={classNames(styles.thumb, styles[node?.maType?.toLocaleLowerCase()])}
              />
              <span className={styles.title}>{node?.maTitle ? node?.maTitle : '-'}</span>
            </div>
          </td>
          <td>
            <span className={styles.creatorName}>{node?.userByMaUploadedBy?.uFullName || '-'}</span>
          </td>
          <td>
            <span className={styles.contentType}>{contentType}</span>
          </td>
          <td>
            <span className={styles.duration}>{formatDurationWithLabelsMs(node?.maDuration)}</span>
          </td>
          <td>
            <span className={styles.date}>{formatDateDDMMYYYY(node?.maCreatedAt)}</span>
          </td>
          <td>
            <span className={styles.statusText}>{node?.maStatus ? node?.maStatus?.toLocaleLowerCase() : '-'}</span>
          </td>
          <td>
            {node?.maStatus?.toLocaleLowerCase() === 'pending' ? (
              <Button
                label="Review"
                color="primary"
                variant="contained"
                className={styles.actionButton}
                onClick={() => handleReview(node.maId, contentType)}
              />
            ) : (
              <Button
                label="Reviewed"
                variant="transparent"
                className={styles.actionButton}
                onClick={() => handleViewDetails(node.maId, contentType)}
              />
            )}
          </td>
        </tr>
      );
    });

    if (loadingMore) {
      rows.push(
        <tr key="loading-more">
          <td colSpan={7}>
            <div className={styles.loadingMore}>
              <Skeleton width="100%" height="16px" />
            </div>
          </td>
        </tr>
      );
    }

    return rows;
  };

  const renderTableHead = () => (
    <thead>
      <tr>
        {TABLE_COLUMNS.map((column) => (
          <th key={column}>{column}</th>
        ))}
      </tr>
    </thead>
  );

  if (error) {
    return <div className={styles.container}>{t('common:error_loading_data')}</div>;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="Back" />
          </IconButton>
          <h1>{t('user:nav.moderate')}</h1>
        </div>
        <ModerateContentFilter
          className={styles.headerFilter}
          appliedDateRange={appliedDateRange}
          appliedContentTypes={appliedContentTypes}
          appliedCategories={appliedCategories}
          onApply={handleApplyFilters}
        />
      </header>

      <div className={classNames(styles.tableWrapper, { [styles.tableWrapperEmpty]: isEmpty })}>
        {isEmpty ? (
          <>
            <div className={styles.tableHeader}>
              <table className={styles.table}>{renderTableHead()}</table>
            </div>
            <div className={styles.emptyState}>
              <NoResultsIcon className={styles.emptyStateIcon} aria-hidden="true" />
              <p className={styles.emptyStateText}>{t('upload:moderate.no_results_found')}</p>
            </div>
          </>
        ) : (
          <div className={styles.tableHorizontalScroll}>
            <div className={styles.tableInner}>
              <div className={styles.tableHeader}>
                <table className={styles.table}>{renderTableHead()}</table>
              </div>
              <div className={styles.tableBodyScroll} onScroll={handleTableScroll}>
                <table className={styles.table}>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModerateContent;
